<template>
  <div class='lib__form__main' data-cy='resume-component'>
    <ValidationObserver ref='validator' slim>
      <div class='lib__form__main__content'>
        <header class='lib__form__main__header'>
          <h2 class='lib__h2'>{{ $t('resume.smartMeter.title') }}</h2>
        </header>

        <resume-section-title :read-only='readOnly' :step='1' minimumBadgeWidth @goToStep='goToStep($event)'>
          <template v-slot:icon>1</template>
          <template>{{ $t('resume.identification') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <ul class='lib__infobox-list'>
              <InfoBox item level='success'>
                <template v-slot:icon>
                  <div class='lib__tooltips lib__tooltips--top-left'>
                    <span>
                      <i class='fas fa-map-marker-alt'></i>
                    </span>
                    <span class='lib__tooltips__data'>{{ $t('identification.smartMeter.addressTooltip') }}</span>
                  </div>
                </template>
                <template>
                  <h3 class='lib__text-uppercase'>{{ $t('identification.smartMeter.address') }}</h3>
                  <p>
                    {{ gridAccessPointState.consumptionAddress.houseNumber }}
                    {{ gridAccessPointState.consumptionAddress.streetName }}
                    {{ gridAccessPointState.consumptionAddress.complement || '' }}
                    {{ gridAccessPointState.consumptionAddress.complementBox || '' }} -
                    {{ gridAccessPointState.consumptionAddress.zipCode }}
                    {{ gridAccessPointState.consumptionAddress.cityName }}
                  </p>
                </template>
              </InfoBox>
              <InfoBox item level='primary'>
                <template v-slot:icon>
                  <img :src="require('@ores/css-library/assets/img/icons/electricity.svg')" alt='electricity' />
                </template>
                <template>
                  <h3 class='lib__text-uppercase'>{{ $t('identification.smartMeter.eanCode') }}</h3>
                  <p>{{ gridAccessPointState.ean }}</p>
                </template>
              </InfoBox>
            </ul>
          </template>
        </resume-container>

        <resume-section-title :read-only='readOnly' :step='2' minimumBadgeWidth noMarginTop
                              @goToStep='goToStep($event)'>
          <template v-slot:icon>2</template>
          <template>{{ $t('resume.contact') }}</template>
        </resume-section-title>
        <resume-container commonMobileAndDesktopLayout>
          <template v-slot:common>
            <resume-contact-info :address='gridAccessPointState.consumptionAddress'
                                 :contact-info='contactInfoState'></resume-contact-info>
          </template>
        </resume-container>

        <resume-section-title :read-only='readOnly' :step='3' minimumBadgeWidth noMarginTop
                              @goToStep='goToStep($event)'>
          <template v-slot:icon>3</template>
          <template>{{ $t('chargingStationDeclaration.information.title') }}</template>
        </resume-section-title>
        <resume-container borderless commonMobileAndDesktopLayout>
          <template v-slot:common>
            <InfoTable v-for='(chargingStation, index) in chargingStationsState' :key='index'>
              <template>
                <InfoTableItem>
                  <template v-slot:label>
                    <b>{{ $t('chargingStationDeclaration.information.station', { count: (index + 1).toString() }) }}</b>
                  </template>
                </InfoTableItem>
                <InfoTableItem>
                  <template v-slot:label>{{ $t('chargingStationDeclaration.summary.declaration') }}</template>
                  <template v-slot:value>
                    {{ chargingStation.inService ? $t('chargingStationDeclaration.summary.inService') : $t('chargingStationDeclaration.summary.outService')
                    }}
                  </template>
                </InfoTableItem>
                <InfoTableItem>
                  <template v-slot:label>{{ $t('chargingStationDeclaration.summary.date') }}</template>
                  <template v-slot:value>{{ displayDate(chargingStation.inServiceDate) }}</template>
                </InfoTableItem>
                <InfoTableItem>
                  <template v-slot:label>{{ $t('chargingStationDeclaration.summary.usage') }}</template>
                  <template v-slot:value>{{ $t(`chargingStationDeclaration.summary.${chargingStation.usage}`) }}
                  </template>
                </InfoTableItem>
                <InfoTableItem>
                  <template v-slot:label>{{ $t('chargingStationDeclaration.summary.bidirectional') }}</template>
                  <template v-slot:value>{{ chargingStation.biDirectional ? $t('common.yes') : $t('common.no') }}
                  </template>
                </InfoTableItem>
                <InfoTableItem>
                  <template v-slot:label>{{ $t('chargingStationDeclaration.summary.smart') }}</template>
                  <template v-slot:value>{{ chargingStation.isSmart ? $t('common.yes') : $t('common.no') }}</template>
                </InfoTableItem>
                <InfoTableItem>
                  <template v-slot:label>{{ $t('chargingStationDeclaration.information.brand') }}</template>
                  <template v-slot:value>{{ chargingStation.brand.brand }}</template>
                </InfoTableItem>
                <InfoTableItem>
                  <template v-slot:label>{{ $t('chargingStationDeclaration.information.model') }}</template>
                  <template v-slot:value>{{ chargingStation.model }}</template>
                </InfoTableItem>
                <InfoTableItem>
                  <template v-slot:label>{{ $t('chargingStationDeclaration.information.power') }}</template>
                  <template v-slot:value>{{ chargingStation.power === -1 ? $t('common.other') : `${chargingStation.power} kW` }}</template>
                </InfoTableItem>
                <InfoTableItem>
                  <template v-slot:label>{{ $t('chargingStationDeclaration.information.plug') }}</template>
                  <template v-slot:value>{{ chargingStation.plug }}</template>
                </InfoTableItem>
              </template>
            </InfoTable>
          </template>
        </resume-container>

      </div>
    </ValidationObserver>
  </div>
</template>

<script lang='ts'>
import { Component, Prop } from 'vue-property-decorator'
import { State } from 'vuex-class'

import Step from '@/components/steps/Step'
import ResumeContactInfo from '@/components/display/Resume/ResumeContactInfo.vue'
import { ChargingStationDeclarationGridAccessPoint, ContactInfo } from '@/models'
import {
  ChargingStationInformation,
  SendChargingStationDeclarationCommand
} from '@/models/charging-station-declaration'
import { dateService } from '@ores/vue-library'
import { AxiosRequestConfig } from 'axios'

@Component({
  name: 'summary-step',
  components: {
    ResumeContactInfo
  }
})
export default class SummaryStep extends Step {
  //#region [Property]
  @Prop({ type: Boolean, default: false }) public readonly readOnly!: boolean
  //#endregion

  //#region [Data]
  @State('gridAccessPoint', { namespace: 'chargingStationDeclaration/identification' })
  public gridAccessPointState!: ChargingStationDeclarationGridAccessPoint
  @State('contactInfo', { namespace: 'chargingStationDeclaration/contactInfo' })
  public contactInfoState!: ContactInfo
  @State('requestId', { namespace: 'chargingStationDeclaration' })
  public requestId!: string
  @State('stations', { namespace: 'chargingStationDeclaration/information' })
  public chargingStationsState!: ChargingStationInformation[]
  @State('lang')
  public language!: string
  //#endregion

  //#region [Methods]
  public displayDate(date: Date): string {
    return dateService.displayDate(date)
  }

  public async save(next: boolean, valid: boolean) {
    if (next && valid) {
      const data: SendChargingStationDeclarationCommand = {
        webReference: this.requestId,
        language: this.language,
        gridAccessPoint: this.gridAccessPointState,
        contactInfo: this.contactInfoState,
        stations: this.chargingStationsState.map((cs) => {
          return {
            power: cs.power !== -1 ? cs.power : null,
            otherPower: cs.power === -1,
            usage: cs.usage!,
            plug: cs.plug!,
            inServiceDate: cs.inServiceDate!,
            biDirectional: cs.biDirectional!,
            inService: cs.inService!,
            isSmart: cs.isSmart!,
            brand: cs.brand!.brand,
            model: cs.model!,

          }
        })
      }

      const token = sessionStorage.getItem('token')

      if (!token) {
        throw new Error('No token found in sessionStorage');
      }
      
      const config: AxiosRequestConfig = { headers: { 'Authorization': token } }
      await this.$api.post('/ChargingStationDeclaration', data, config)

      this.$appInsights.trackEvent('ChargingStationDeclaration_StepSummary_Validated', {
        Segment: 'ChargingStationDeclaration',
        WebRef: this.requestId
      })
    }
  }

  public goToStep(step: number) {
    this.$emit('goToStep', step)
  }

  //#endregion
}
</script>
